import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Sun, Cloud, CloudRain, CloudSnow, CloudLightning, Wind, Drop, ThermometerSimple } from '@phosphor-icons/react';

const WEATHER_ICONS = {
  0: Sun, 1: Sun, 2: Cloud, 3: Cloud,
  45: Cloud, 48: Cloud, 51: CloudRain, 53: CloudRain,
  55: CloudRain, 61: CloudRain, 63: CloudRain, 65: CloudRain,
  71: CloudSnow, 73: CloudSnow, 75: CloudSnow,
  80: CloudRain, 81: CloudRain, 82: CloudRain, 95: CloudLightning
};

const RATING_COLORS = {
  Excellent: 'text-emerald-600', Good: 'text-primary', Fair: 'text-amber-600', Poor: 'text-red-500'
};

export default function WeatherWidget() {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        api.get(`/weather?lat=${pos.coords.latitude}&lng=${pos.coords.longitude}`)
          .then(({ data }) => setWeather(data))
          .catch(() => {})
          .finally(() => setLoading(false));
      },
      () => {
        // Default to Augusta, GA if no location
        api.get('/weather?lat=33.50&lng=-82.02')
          .then(({ data }) => setWeather(data))
          .catch(() => {})
          .finally(() => setLoading(false));
      },
      { timeout: 5000 }
    );
  }, []);

  if (loading) {
    return (
      <div className="widget-card animate-pulse" data-testid="weather-widget-loading">
        <div className="h-4 bg-muted rounded w-24 mb-3" />
        <div className="h-10 bg-muted rounded w-16 mb-2" />
        <div className="h-3 bg-muted rounded w-32" />
      </div>
    );
  }

  if (!weather) return null;

  const Icon = WEATHER_ICONS[weather.current.weather_code] || Sun;
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="widget-card" data-testid="weather-widget">
      {/* Time */}
      <div className="text-center mb-4">
        <p className="text-3xl font-serif font-semibold text-foreground tracking-tight" data-testid="weather-time">
          {time.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
        </p>
        <p className="text-xs text-muted-foreground font-sans mt-0.5">
          {time.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
        </p>
      </div>

      <div className="h-px bg-border mb-4" />

      {/* Current Conditions */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-4xl font-serif font-semibold text-foreground" data-testid="weather-temp">
            {weather.current.temperature}°
          </p>
          <p className="text-sm text-muted-foreground font-sans mt-0.5">{weather.current.description}</p>
        </div>
        <Icon size={40} weight="regular" className="text-primary/70" />
      </div>

      <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground font-sans">
        <span className="flex items-center gap-1"><Wind size={12} /> {weather.current.wind_speed} mph</span>
        <span className={`font-semibold ${RATING_COLORS[weather.current.golf_rating]}`} data-testid="weather-golf-rating">
          Golf: {weather.current.golf_rating}
        </span>
      </div>

      <div className="h-px bg-border my-4" />

      {/* 7-Day Forecast */}
      <p className="text-[10px] text-muted-foreground uppercase tracking-[0.15em] font-sans font-semibold mb-3">
        7-Day Golf Outlook
      </p>
      <div className="space-y-2" data-testid="weather-forecast">
        {weather.forecast.map((day, i) => {
          const DayIcon = WEATHER_ICONS[day.weather_code] || Sun;
          const d = new Date(day.date + 'T12:00:00');
          return (
            <div key={day.date} className="flex items-center justify-between text-xs font-sans">
              <span className="w-8 text-muted-foreground">{i === 0 ? 'Today' : dayNames[d.getDay()]}</span>
              <DayIcon size={14} weight="regular" className="text-foreground/60" />
              <span className="text-foreground">{day.low}° / {day.high}°</span>
              <span className={`font-semibold text-[10px] w-16 text-right ${RATING_COLORS[day.golf_rating]}`}>
                {day.golf_rating}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
